import {
    Heading,
    Notification,
    NotificationsGroup,
    Paragraph,
    Table,
    Tag,
    Pagination,
} from '@vwfs-bronson/bronson-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersSection } from '../filters-section/FiltersSection';
import { RequestHistoryFilters } from '../filters-section/RequestHistoryFiltersSlice';
import { Formik } from 'formik';
import { CaseItem, Contract, ContractDetails } from 'cpfr-common';
import { RootState } from '../../../store/rootReducer';
import { useSelector } from 'react-redux';
import { NoConnectionNotification } from '../../notifications/no-connection/NoConnectionNotification';

const initialFilters: RequestHistoryFilters = {
    contractFilter: 'all',
    dateFilter: 'all',
    statusFilter: 'all',
    cleared: false,
};

export const RequestHistoryUi: React.FC<{
    cases: CaseItem[] | undefined;
    contracts: (Contract & ContractDetails)[];
}> = ({ cases, contracts }) => {
    const { t } = useTranslation('request-history');

    if (!cases) return <NoConnectionNotification />;

    return (
        <>
            <Heading level={1}>{t('title')}</Heading>
            <Paragraph>{t('subtitle')}</Paragraph>
            <Formik initialValues={initialFilters} onSubmit={() => undefined}>
                <>
                    <FiltersSection contracts={contracts} />
                </>
            </Formik>
            <RequestHistoryTable cases={cases} />
        </>
    );
};

const Status = ({ status }: { status: CaseItem['status'] }) => {
    switch (status?.toLowerCase()) {
        case 'traitée':
            return <Tag success>{status}</Tag>;
        case 'annulée':
            return <Tag error>{status}</Tag>;
        case 'a traiter':
            return <Tag>{status}</Tag>;
        case 'en cours de traitement':
            return <Tag warning>{status}</Tag>;
        default:
            return <Tag>{status}</Tag>;
    }
};

export const RequestHistoryTable: React.FC<{ cases?: CaseItem[] }> = ({ cases }) => {
    const { t } = useTranslation('request-history');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const allFilters = useSelector((state: RootState) => state.requestHistoryFilters);

    const hasFiltersApplied = Object.entries(allFilters)
        ?.filter((singleFilter) => singleFilter[0] !== 'cleared')
        .some((singleFilter) => singleFilter[1] !== 'all');

    const filteredCases = useMemo(() => {
        if (!cases) return [];

        return cases.filter((singleCase) => {
            if (allFilters.contractFilter !== 'all' && singleCase.contractNumber !== allFilters.contractFilter) {
                return false;
            }
            if (
                allFilters.statusFilter !== 'all' &&
                singleCase.status?.toLowerCase() !== allFilters.statusFilter.toLowerCase()
            ) {
                return false;
            }
            if (allFilters.dateFilter !== 'all' && singleCase.creationDate) {
                const monthsMap: Record<string, number> = {
                    last3Months: 3,
                    last6Months: 6,
                    last12Months: 12,
                };
                const months = monthsMap[allFilters.dateFilter];
                const cutoffDate = new Date();
                cutoffDate.setMonth(cutoffDate.getMonth() - months);

                if (new Date(singleCase.creationDate) < cutoffDate) {
                    return false;
                }
            }

            return true;
        });
    }, [cases, allFilters]);

    const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
    const paginationStartIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        setCurrentPage(1);
    }, [allFilters]);

    useEffect(() => {
        if (totalPages === 0) {
            if (currentPage !== 1) {
                setCurrentPage(1);
            }
            return;
        }

        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedCases = useMemo(() => {
        return filteredCases.slice(paginationStartIndex, paginationStartIndex + itemsPerPage);
    }, [filteredCases, paginationStartIndex, itemsPerPage]);

    if (filteredCases.length === 0 && !hasFiltersApplied)
        return (
            <NotificationsGroup className="u-mb">
                <Notification
                    status="warning"
                    title={t('table.no-entry-found')}
                    visible
                    showCloseButton={false}
                    aria-atomic="true"
                    aria-live="polite"
                    role="status"
                >
                    <React.Fragment key=".0">{t('table.no-results')}</React.Fragment>
                </Notification>
            </NotificationsGroup>
        );

    return (
        <>
            <Table wide>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th scope="col" style={{ width: '200px' }}>
                            {t('table.contract-number')}
                        </Table.Th>
                        <Table.Th scope="col">{t('table.type-of-request')}</Table.Th>
                        <Table.Th scope="col" style={{ width: '250px' }}>
                            {t('table.status')}
                        </Table.Th>
                        <Table.Th scope="col" style={{ width: '200px' }}>
                            {t('table.date')}
                        </Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {filteredCases.length === 0 && hasFiltersApplied ? (
                        <Table.Tr>
                            <Table.Td />
                            <Table.Td>
                                <p className="u-mt-small">{t('table.no-results-with-filter')}</p>
                            </Table.Td>
                            <Table.Td />
                            <Table.Td />
                        </Table.Tr>
                    ) : (
                        paginatedCases.map((singleCase, index) => (
                            <Table.Tr
                                key={`${singleCase.creationDate || 'no-date'}-${
                                    singleCase.contractNumber || 'no-contract'
                                }-${singleCase.object || 'no-object'}-${paginationStartIndex + index}`}
                            >
                                <Table.Td>{singleCase.contractNumber}</Table.Td>
                                <Table.Td>{singleCase.object}</Table.Td>
                                <Table.Td>
                                    <Status status={singleCase.status} />
                                </Table.Td>
                                <Table.Td>{new Date(singleCase.creationDate || '').toLocaleDateString()}</Table.Td>
                            </Table.Tr>
                        ))
                    )}
                </Table.Tbody>
            </Table>
            {filteredCases.length > itemsPerPage && (
                <Pagination
                    className="u-mt"
                    title={t('table.pagination.title', { defaultValue: 'Pagination' })}
                    prevText={t('table.pagination.previous', { defaultValue: 'Previous' })}
                    nextText={t('table.pagination.next', { defaultValue: 'Next' })}
                    prevDisabled={currentPage === 1}
                    nextDisabled={currentPage === totalPages}
                    onPrevClick={() => setCurrentPage((prevPage) => prevPage - 1)}
                    onNextClick={() => setCurrentPage((prevPage) => prevPage + 1)}
                >
                    {Array.from({ length: totalPages }, (_, index) => {
                        const pageNumber = index + 1;

                        return (
                            <Pagination.Item
                                key={pageNumber}
                                active={currentPage === pageNumber}
                                onClick={() => setCurrentPage(pageNumber)}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        );
                    })}
                </Pagination>
            )}
        </>
    );
};
